import { describeApiError, type FantasyContest, type Leaderboard } from '@brush/api-client';
import { ErrorNotice } from '@brush/ui';
import { useState } from 'react';
import { changedFrom } from '../../dialogDrafts';
import { useCreateFantasyContest } from '../../queries';
import { useRoom } from '../../room';
import { useUnsavedWork } from '../../unsaved';
import { Modal, btnGhost, btnPrimary, modalInput } from '../Modal';

/**
 * §5's reference contest, which is the format v1 implements (§2).
 *
 * Eight players under a budget of 200. These are the derivative of the 25K Fantasy auction — the
 * free-to-enter salary cap game PokerOrg runs off it — and they are a starting point rather than a
 * rule: every one of them is editable in section 2 before the contest publishes.
 */
const REFERENCE = {
  rosterSize: 8,
  budget: 200,
} as const;

/**
 * Creating a contest asks two questions and defaults the rest.
 *
 * <b>Name and source leaderboard, and nothing else.</b> The create request takes a roster size, a
 * budget, an entry fee and a captain rule as well, and every one of them has a defensible default
 * that section 2 then puts in front of the operator with a worked example beside it. Asking for
 * them here would be a second, worse copy of that section — asked before the operator has seen
 * what the contest is, and without the pool to judge a budget against.
 *
 * What cannot be defaulted is the board: a contest inherits its events and its scoring from one
 * (locked rule 1), so it is the decision that makes the contest a contest. Same shape as
 * `NewSalaryModelDialog`: the smallest thing that can exist, created and opened.
 */
export function NewContestDialog({
  leaderboards,
  onClose,
  onCreated,
}: {
  leaderboards: readonly Leaderboard[];
  onClose: () => void;
  /** Hands back the new contest so the list can open the builder on it straight away. */
  onCreated: (contest: FantasyContest) => void;
}) {
  const { room, roomId } = useRoom();
  const create = useCreateFantasyContest(roomId);

  const [name, setName] = useState('');
  const [leaderboardId, setLeaderboardId] = useState(() => leaderboards[0]?.id ?? '');

  // #591: a name typed or a board chosen asks before a room switch, or any other way out, discards it.
  const [opened] = useState(() => ({ name, leaderboardId }));
  useUnsavedWork(changedFrom(opened, { name, leaderboardId }));

  const board = leaderboards.find((l) => l.id === leaderboardId) ?? null;
  const ready = name.trim().length > 0 && leaderboardId !== '' && !!roomId;

  const submit = () => {
    if (!ready) return;

    create.mutate(
      {
        roomId: roomId!,
        leaderboardId,
        name: name.trim(),
        description: null,
        // Explicit rather than left to the server's own default, so the contest the operator lands
        // on in section 2 says what it is rather than what happened to be filled in.
        rosterFormat: 'salary_cap',
        rosterSize: REFERENCE.rosterSize,
        budget: REFERENCE.budget,
        budgetUnitLabel: null,
        captainEnabled: false,
        captainMultiplier: null,
        // §12 defers paid entry: `EntryFee` is modelled and constrained to zero, so this is the
        // only value the server accepts and there is no control for it anywhere.
        entryFee: 0,
        currency: null,
        maxEntriesPerPlayer: 1,
        // Ignored by the server — a contest is always created Draft, which is the only way into
        // §6's lifecycle. Sent as null rather than omitted because these contracts are
        // non-optional-with-null.
        status: null,
      },
      { onSuccess: onCreated },
    );
  };

  return (
    <Modal
      title="New contest"
      subtitle={`Run by ${room?.name ?? 'this room'}. It stays a draft until you publish it.`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!ready || create.isPending}
            className={btnPrimary}
          >
            {create.isPending ? 'Creating…' : 'Create and open'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-xs text-ink-800/60">Name</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="Summer Series Fantasy"
            className={modalInput}
          />
          <span className="mt-1 block text-[11px] text-ink-800/45">
            What entrants will see. Name it after the series it follows.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-xs text-ink-800/60">Scores from</span>
          <select
            value={leaderboardId}
            onChange={(e) => setLeaderboardId(e.target.value)}
            className={modalInput}
          >
            {leaderboards.map((board) => (
              <option key={board.id} value={board.id}>
                {board.name} · {board.dateStart} to {board.dateEnd}
              </option>
            ))}
          </select>
          <span className="mt-1 block text-[11px] leading-relaxed text-ink-800/45">
            The contest takes its events and its scoring from this leaderboard. Whatever a player
            scores there is what they score for the teams that drafted them.
          </span>
        </label>

        {/* A board that has already closed is a legitimate thing to pick — and it is also the one
            case that cannot be published, since every event in it has run (§6.1). Said now rather
            than at the publish refusal, which is two screens and several minutes later. */}
        {board?.status === 'closed' ? (
          <p className="border border-line border-l-4 border-l-warn bg-surface p-3 text-[11.5px] leading-relaxed text-ink-900">
            <strong className="text-ink-950">{board.name} has closed.</strong> Its events have all
            run, so this contest will not publish — entrants would be drafting with the results in
            front of them. Pick a series that has not started, or set this one up and change the
            board in section 1 later.
          </p>
        ) : null}

        <p className="text-xs leading-relaxed text-ink-800/60">
          Starts as a {REFERENCE.rosterSize}-player roster under a budget of {REFERENCE.budget},
          which is the format the reference contest uses. You set the budget, the roster size, the
          captain rule and every price on the next screen — nothing is fixed until you publish.
        </p>

        <ErrorNotice message={describeApiError(create.error)} />
      </div>
    </Modal>
  );
}
